import { Injectable } from '@angular/core';
import {
  AutoCompleteResponse,
  AutoCompleteResultByCategory,
  AutocompleteCategory,
  AutocompleteRequest,
  EventInfoRequest,
  EventInfoResponse,
  PerformanceResult,
  PerformerInfoRequest,
  PerformerInfoResponse,
  SearchEventsRequest,
  SearchEventsResponse,
  SearchPaginationRequest,
  SearchPaginationResponse,
  SearchRequest,
  SearchResponse,
  VenueInfoRequest,
  VenueInfoResponse,
} from '@grpc-clients/external/mercury/v1/entertainment/entertainment.pb';
import { Geolocation } from '@grpc-clients/external/mercury/v1/shared/geolocation.pb';
import { EntertainmentClient } from '@grpc-clients/services/entertainments-service/api/entertainment.pbsc';
import { Store } from '@ngrx/store';
import { SiteSettingsService } from '@shared/services/site-settings.service';
import { ISiteSettings } from '@shared/shared/interfaces/site-settings.interfaces';
import { attributeIdMapper, ribbonTypeMapper } from '@shared/shared/modules/card/models/card';
import { Location, LocationService } from '@shared/shared/services/location-service';
import { GetTenEvents } from '@shared/store/entertainment';
import { USER_LOCAL_STORAGE_KEY } from 'app.config';
import { Observable, catchError, filter, map, of, take } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class EntertainmentTicketSearchService {
  private browserGeoLocation: Location = null;
  public siteSettings$: Observable<ISiteSettings> = this.siteSettingsService.fetchSiteSettings();

  constructor(
    private searchClient: EntertainmentClient,
    private ngrxStore: Store,
    private siteSettingsService: SiteSettingsService,
    private locationService: LocationService
  ) {
    const isUserLoggedIn = localStorage.getItem(USER_LOCAL_STORAGE_KEY);
    if (isUserLoggedIn) {
      this.locationService.getLocation(Infinity).subscribe({
        next: (location) => {
          this.browserGeoLocation = location;
          localStorage.setItem('browserGeoLocation', JSON.stringify(location));
          this.dispatchGetTenEvents();
        },
        error: () => {
          this.browserGeoLocation = undefined;
          localStorage.removeItem('browserGeoLocation');
          this.dispatchGetTenEvents();
        },
      });
    }
  }

  private dispatchGetTenEvents() {
    this.siteSettings$
      .pipe(
        filter((settings) => !!settings.id && settings.switches.entertainment_redesign_phase_1_1),
        take(1)
      )
      .subscribe(() => {
        setTimeout(() => this.ngrxStore.dispatch(new GetTenEvents()));
      });
  }

  public getEventAutoSuggest(
    search: string,
    languageCode: string,
    geolocation: Geolocation
  ): Observable<AutoCompleteResultByCategory[]> {
    const request = new AutocompleteRequest({
      languageCode,
      categories: [AutocompleteCategory.EVENT, AutocompleteCategory.EVENT_PERFORMER, AutocompleteCategory.EVENT_VENUE],
      searchString: search,
      geolocation,
    }); // tenantId, brandId and salesChannelId is added by BE

    return this.searchClient.autocomplete(request).pipe(
      map((response: AutoCompleteResponse) => response.categories || []),
      catchError(() => of([]))
    );
  }

  public search(request: SearchRequest): Observable<SearchResponse> {
    // tenantId, brandId and salesChannelId is added by BE
    return this.searchClient.search(request).pipe(
      map((response: SearchResponse) => response),
      catchError(() => of(null))
    );
  }

  public getTicketEventResult(payload: {
    languageCode: string;
    eventType: AutocompleteCategory;
    eventId: number;
  }): Observable<EventInfoResponse | VenueInfoResponse | PerformerInfoResponse> {
    let request;
    let searchRequest;
    const requestData = {
      languageCode: payload.languageCode,
    };
    const eventIds = [payload.eventId];

    if (payload.eventType === AutocompleteCategory.EVENT) {
      requestData['eventIds'] = eventIds;
      request = new EventInfoRequest(requestData); // tenantId, brandId and salesChannelId is added by BE
      searchRequest = this.searchClient.eventInfo(request);
    }

    if (payload.eventType === AutocompleteCategory.EVENT_PERFORMER) {
      requestData['performerIds'] = eventIds;
      request = new PerformerInfoRequest(requestData); // tenantId, brandId and salesChannelId is added by BE
      searchRequest = this.searchClient.performerInfo(request);
    }

    if (payload.eventType === AutocompleteCategory.EVENT_VENUE) {
      requestData['venueIds'] = eventIds;
      request = new VenueInfoRequest(requestData); // tenantId, brandId and salesChannelId is added by BE
      searchRequest = this.searchClient.venueInfo(request);
    }

    return searchRequest.pipe(
      map((response: EventInfoResponse | VenueInfoResponse | PerformerInfoResponse) => response),
      catchError(() => of(null))
    );
  }

  public paginatedResults(searchToken: string, pageIndex: number): Observable<SearchPaginationResponse> {
    // tenantId, brandId and salesChannelId is added by BE
    const request = new SearchPaginationRequest({
      searchToken,
      pageIndex,
    });
    return this.searchClient.searchPagination(request).pipe(
      map((response: SearchPaginationResponse) => response),
      catchError(() => of(null))
    );
  }

  public getTenEvents(request: SearchEventsRequest): Observable<SearchEventsResponse> {
    return this.searchClient.searchTenEvents(request).pipe(
      map((response: SearchEventsResponse) => response),
      catchError(() => of(null))
    );
  }

  public mapToResults(results: PerformanceResult[]): PerformanceResult[] {
    if (!results) {
      return [];
    }
    return results.map((item: PerformanceResult) => {
      const ribbonText = attributeIdMapper[item.attributeIds[0]];
      const ribbonType = ribbonTypeMapper[item.attributeIds[0]];
      const ribbon: { filled: boolean; text: string; type: string } = ribbonText
        ? { filled: true, text: ribbonText, type: ribbonType }
        : null;
      const resultWithRibbon = { ...JSON.parse(JSON.stringify(item)), ribbon };
      return resultWithRibbon as unknown as PerformanceResult;
    });
  }

  public getBrowserGeolocation(): Location {
    const localStorageGeoLocation = localStorage.getItem('browserGeoLocation');
    if (this.browserGeoLocation === null && localStorageGeoLocation) {
      return JSON.parse(localStorageGeoLocation) as Location;
    }
    return this.browserGeoLocation;
  }

  public defaultGeolocation(): Geolocation.AsObject {
    return {
      latitude: { value: '51.507290765043294' },
      longitude: { value: '-0.129816679839094' },
    } as Geolocation.AsObject;
  }
}
